import React from "react";
import { eur, frDate, DEFAULT_DOC_TEMPLATE } from "@/lib/constants";
import { StoredImg } from "@/components/stock/FileField";

const initial = (s) => (s || "E").slice(0, 1).toUpperCase();

const Block = ({ b, primary, org, quote, contact }) => {
  if (b.visible === false) return null;
  const lines = quote?.lines || [];
  switch (b.type) {
    case "brand":
      return (
        <div data-testid="doc-block-brand" className="mb-3">
          {org?.logo ? (
            <img src={org.logo} alt="" className="h-12 object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg text-lg font-bold text-white" style={{ background: primary }}>{initial(org?.name)}</div>
              <span className="text-lg font-bold">{org?.name}</span>
            </div>
          )}
          {(org?.address || org?.siret) && (
            <div className="mt-1 text-xs text-gray-500">{org?.address}{org?.siret ? ` · SIRET ${org.siret}` : ""}</div>
          )}
        </div>
      );
    case "doc_info":
      return (
        <div data-testid="doc-block-doc_info" className="mb-3">
          <p className="text-sm font-semibold" style={{ color: primary }}>{quote?.is_invoice ? "FACTURE" : "DEVIS"} {(quote?.is_invoice ? quote?.invoice_number : quote?.number) || ""}</p>
          <p className="text-xs text-gray-500">{frDate(quote?.created_at || new Date().toISOString())}</p>
          {quote?.valid_until && <p className="text-xs text-gray-500">Valable jusqu'au {frDate(quote.valid_until)}</p>}
        </div>
      );
    case "client":
      return (
        <div data-testid="doc-block-client" className="mb-3">
          <p className="text-xs uppercase text-gray-400">{b.title || "Client"}</p>
          <p className="font-medium">{contact?.name || "—"}</p>
          {contact?.email && <p className="text-xs text-gray-500">{contact.email}</p>}
        </div>
      );
    case "lines":
      return (
        <table data-testid="doc-block-lines" className="mb-3 w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs text-gray-400">
              <th className="pb-2">Désignation</th><th className="pb-2 text-center">Qté</th><th className="pb-2 text-right">PU</th><th className="pb-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              l.line_type === "text" ? (
                <tr key={i} className="border-b border-gray-100"><td colSpan={4} className="pt-3 pb-1 text-sm font-semibold text-gray-700">{l.name}</td></tr>
              ) : l.line_type === "essential" ? (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-1 pl-5 text-xs text-gray-400">↳ {l.name} <span className="text-gray-300">· inclus</span></td>
                  <td className="py-1 text-center text-xs text-gray-400">{l.qty}</td>
                  <td></td><td></td>
                </tr>
              ) : (
              <tr key={i} className="border-b border-gray-100">
                <td className="py-2"><p className="font-medium">{l.name || "…"}</p>{l.description && <p className="text-xs text-gray-400">{l.description}</p>}{(l.custom_selections || []).filter((s) => s.value).map((s, si) => <p key={si} className="text-xs text-gray-500">{s.label} : <span className="font-medium text-gray-700">{s.value}</span></p>)}</td>
                <td className="py-2 text-center">{l.qty}</td>
                <td className="py-2 text-right">{eur(l.price)}</td>
                <td className="py-2 text-right">{eur((l.qty || 0) * (l.price || 0))}</td>
              </tr>
              )
            ))}
          </tbody>
        </table>
      );
    case "totals":
      return (
        <div data-testid="doc-block-totals" className="mb-3 ml-auto w-56 space-y-1 text-sm">
          <div className="flex justify-between text-gray-500"><span>Sous-total</span><span>{eur(quote?.subtotal)}</span></div>
          <div className="flex justify-between text-gray-500"><span>TVA ({quote?.tax_rate || 0}%)</span><span>{eur(quote?.tax)}</span></div>
          <div className="flex justify-between border-t border-gray-200 pt-1 text-base font-bold"><span>Total</span><span>{eur(quote?.total)}</span></div>
        </div>
      );
    case "payment":
      return (
        <div data-testid="doc-block-payment" className="mb-3 rounded-lg bg-gray-50 p-3 text-xs">
          <p className="mb-1 font-semibold" style={{ color: primary }}>{b.title || "Conditions de paiement"}</p>
          <div className="flex justify-between"><span>Acompte ({quote?.deposit_percent || 0}%)</span><span className="font-semibold">{eur(quote?.deposit)}</span></div>
          <div className="flex justify-between"><span>Solde (J-{quote?.balance_days_before || 0})</span><span className="font-semibold">{eur(quote?.balance)}</span></div>
        </div>
      );
    case "notes":
      if (!quote?.notes) return null;
      return <div data-testid="doc-block-notes" className="mb-3 text-xs text-gray-500"><p className="font-semibold text-gray-700">{b.title || "Notes"}</p>{quote.notes}</div>;
    case "legal": {
      const txt = b.text || org?.cancellation_policy || "";
      if (!txt) return null;
      return <div data-testid="doc-block-legal" className="mb-3 text-[10px] text-gray-400"><p className="font-semibold text-gray-600">{b.title || "Mentions légales"}</p>{txt}</div>;
    }
    case "signature":
      return (
        <div data-testid="doc-block-signature" className="mb-3 mt-6 text-xs text-gray-500">
          <p>{b.title || "Bon pour accord"}</p>
          <div className="mt-10 w-48 border-t border-gray-300" />
          <p className="mt-1">Date & signature</p>
        </div>
      );
    case "footer":
      return <div data-testid="doc-block-footer" className="mt-4 border-t border-gray-100 pt-3 text-center text-[10px] text-gray-400">{b.text || "Propulsé par HalgarOS"}</div>;
    default:
      return null;
  }
};

export const DocumentPreview = ({ template, org, quote, contact }) => {
  const tpl = template && template.blocks ? template : DEFAULT_DOC_TEMPLATE;
  const primary = tpl.primary_color || org?.brand_color || "#FF6A00";
  const opening = quote?.opening_image || tpl.opening_image;
  const closing = quote?.closing_image || tpl.closing_image;
  const pres = quote?.presentation_images || [];
  return (
    <div data-testid="document-preview" className="rounded-xl bg-white p-8 text-black shadow-2xl">
      {opening && <StoredImg value={opening} alt="Page d'ouverture" data-testid="doc-opening-image" className="mb-6 w-full rounded-lg border border-gray-200" />}
      {pres.map((p, i) => <img key={i} src={p} alt="Présentation" className="mb-6 w-full rounded-lg border border-gray-200" />)}
      {(tpl.blocks || []).map((b, i) => (
        <Block key={i} b={b} primary={primary} org={org} quote={quote} contact={contact} />
      ))}
      {(org?.siret || org?.vat_number || org?.legal_mentions || (org?.fiscal_status === "societe" && org?.share_capital)) && (
        <div className="mt-6 border-t border-gray-200 pt-2 text-center text-[10px] text-gray-400">
          <div>{[org?.name, org?.siret && `SIRET ${org.siret}`, org?.vat_number && `TVA ${org.vat_number}`, (org?.fiscal_status === "societe" && org?.share_capital) && `Capital ${org.share_capital}`].filter(Boolean).join(" · ")}</div>
          {org?.legal_mentions && <div className="mt-0.5">{org.legal_mentions}</div>}
        </div>
      )}
      {closing && <StoredImg value={closing} alt="Page de clôture" data-testid="doc-closing-image" className="mt-6 w-full rounded-lg border border-gray-200" />}
    </div>
  );
};

export default DocumentPreview;
